import { Toaster } from "sonner";
import { lazy, Suspense } from "react";
import type { ReactNode } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
} from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CashEntryProvider } from "@/contexts/CashEntryContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import FeatureProtectedRoute from "@/components/auth/FeatureProtectedRoute";
import type { FeatureId } from "@/lib/featureAccess";
import Layout from "./components/layout";
import RouteFallback from "@/components/RouteFallback";
import LazyRouteBoundary from "@/components/LazyRouteBoundary";
import HomePage from "./pages/home";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { CatalogProvider } from "./contexts/CatalogContext";
import { NavigationHistoryProvider } from "@/contexts/NavigationHistoryContext";

// Every screen except the home tiles is loaded on demand so that the shell
// (login, layout, home) ships without stats/recharts, kanban/dnd-kit,
// calendars, etc. Each `import()` becomes its own chunk, precached by the
// service worker like any other asset.
const CommandesPage = lazy(() => import("./pages/commandes"));
const ConnectChatGPTPage = lazy(() => import("./pages/connect-chatgpt"));
const ClientsPage = lazy(() => import("./pages/clients"));
const ClientDetailPage = lazy(() => import("./pages/client-detail"));
const ContactDetailPage = lazy(() => import("./pages/contact-detail"));
const OrderDetailPage = lazy(() => import("./pages/order-detail"));
const AbonnementsPage = lazy(() => import("./pages/abonnements"));
const AbonnementDetailPage = lazy(() => import("./pages/abonnement-detail"));
const EvenementsPage = lazy(() => import("./pages/evenements"));
const EvenementDetailPage = lazy(() => import("./pages/evenement-detail"));
const EvenementTaskDetailPage = lazy(
  () => import("./pages/evenement-task-detail"),
);
const PreparationDetailPage = lazy(() => import("./pages/preparation-detail"));
const ValidationsAbonnementsPage = lazy(
  () => import("./pages/validations-abonnements"),
);
const AchatsFleursPage = lazy(() => import("./pages/achats-fleurs"));
const MesTachesPage = lazy(() => import("./pages/mes-taches"));
const CalendrierPage = lazy(() => import("./pages/calendrier"));
const CongesPage = lazy(() => import("./pages/conges"));
const LivraisonsPage = lazy(() => import("./pages/livraisons"));
const GestionLivraisonsPage = lazy(() => import("./pages/gestion-livraisons"));
const MediasPage = lazy(() => import("./pages/medias"));
const EquipePage = lazy(() => import("./pages/equipe"));
const VerificationsPage = lazy(() => import("./pages/verifications"));
const ParametresPage = lazy(() => import("./pages/parametres"));
const CatalogPage = lazy(() => import("./pages/catalog"));
const CommandesOuvertesPage = lazy(() => import("./pages/commandes-ouvertes"));
const CommandesListingPage = lazy(() => import("./pages/commandes-listing"));
const CaissesPage = lazy(() => import("./pages/caisses"));
const StatsPage = lazy(() => import("./pages/stats"));
const RecurringOrdersPage = lazy(() => import("./pages/recurring-orders"));

function PushNotificationInitializer() {
  usePushNotifications();
  return null;
}

function featureRoute(feature: FeatureId, children: ReactNode) {
  return (
    <FeatureProtectedRoute feature={feature}>{children}</FeatureProtectedRoute>
  );
}

/**
 * Single Suspense boundary for all lazily loaded pages, rendered inside the
 * Layout so the top bar stays visible while a chunk downloads. The boundary
 * also recovers from stale chunk names after a deploy.
 */
function LazyRoutes() {
  return (
    <LazyRouteBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </LazyRouteBoundary>
  );
}

/** `/clients/:id` used to 404: the detail screen requires a tab segment. */
function ClientDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/clients/${id}/details`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <CashEntryProvider>
        <CatalogProvider>
          <BrowserRouter>
            <NavigationHistoryProvider>
              <ProtectedRoute>
                <PushNotificationInitializer />
                <Toaster
                  position="top-center"
                  richColors
                  closeButton
                  toastOptions={{ duration: 5000 }}
                />
                <Routes>
                  <Route path="/connect/chatgpt" element={
                    <Suspense fallback={<RouteFallback />}><ConnectChatGPTPage /></Suspense>
                  } />
                  <Route path="/" element={<Layout />}>
                  <Route element={<LazyRoutes />}>
                  <Route index element={<HomePage />} />
                  <Route
                    path="verifications"
                    element={featureRoute(
                      "verifications",
                      <VerificationsPage />,
                    )}
                  />
                  <Route
                    path="commandes"
                    element={featureRoute("orderTracking", <CommandesPage />)}
                  />
                  <Route
                    path="commandes-recurrentes"
                    element={featureRoute("recurringOrders", <RecurringOrdersPage />)}
                  />
                  <Route
                    path="commandes/:id"
                    element={featureRoute("orderTracking", <OrderDetailPage />)}
                  />
                  <Route
                    path="commandes/:id/:tab"
                    element={featureRoute("orderTracking", <OrderDetailPage />)}
                  />
                  <Route
                    path="clients"
                    element={featureRoute("clients", <ClientsPage />)}
                  />
                  <Route
                    path="clients/contacts/:id"
                    element={featureRoute("clients", <ContactDetailPage />)}
                  />
                  <Route
                    path="clients/:id/:tab"
                    element={featureRoute("clients", <ClientDetailPage />)}
                  />
                  <Route path="clients/:id" element={<ClientDetailRedirect />} />
                  <Route
                    path="abonnements"
                    element={featureRoute("subscriptions", <AbonnementsPage />)}
                  />
                  <Route
                    path="abonnements/:id"
                    element={featureRoute("subscriptions", <AbonnementDetailPage />)}
                  />
                  <Route
                    path="evenements"
                    element={featureRoute("events", <EvenementsPage />)}
                  />
                  {/* Event tasks are reached from "Mes tâches" and push deep links
                      by Preparateur/Livreur, who do not have the `events` feature. */}
                  <Route
                    path="evenements/tasks/:taskId"
                    element={featureRoute("myTasks", <EvenementTaskDetailPage />)}
                  />
                  <Route
                    path="evenements/:id"
                    element={featureRoute("events", <EvenementDetailPage />)}
                  />
                  <Route
                    path="evenements/:evenementId/tasks/:taskId"
                    element={featureRoute("myTasks", <EvenementTaskDetailPage />)}
                  />
                  <Route path="preparations" element={<Navigate to="/mes-taches" replace />} />
                  <Route
                    path="preparations/:id"
                    element={featureRoute("myTasks", <PreparationDetailPage />)}
                  />
                  <Route
                    path="validations-abonnements"
                    element={featureRoute(
                      "workshopValidations",
                      <ValidationsAbonnementsPage />,
                    )}
                  />
                  <Route
                    path="achats-fleurs"
                    element={featureRoute("flowerPurchases", <AchatsFleursPage />)}
                  />
                  <Route
                    path="mes-taches"
                    element={featureRoute("myTasks", <MesTachesPage />)}
                  />
                  <Route
                    path="calendrier"
                    element={featureRoute("calendar", <CalendrierPage />)}
                  />
                  <Route
                    path="conges"
                    element={featureRoute("leave", <CongesPage />)}
                  />
                  <Route
                    path="livraisons"
                    element={featureRoute("deliveries", <LivraisonsPage />)}
                  />
                  <Route
                    path="gestion-livraisons"
                    element={featureRoute(
                      "deliveryManagement",
                      <GestionLivraisonsPage />,
                    )}
                  />
                  <Route
                    path="medias"
                    element={featureRoute("media", <MediasPage />)}
                  />
                  <Route
                    path="medias/:imageId"
                    element={featureRoute("media", <MediasPage />)}
                  />
                  <Route
                    path="equipe"
                    element={featureRoute("team", <EquipePage />)}
                  />
                  <Route
                    path="parametres"
                    element={featureRoute("settings", <ParametresPage />)}
                  />
                  <Route
                    path="catalogue"
                    element={featureRoute("catalog", <CatalogPage />)}
                  />
                  <Route path="catalog" element={<Navigate to="/catalogue" replace />} />
                  <Route
                    path="commandes-ouvertes"
                    element={featureRoute("openOrders", <CommandesOuvertesPage />)}
                  />
                  <Route
                    path="commandes-en-lot"
                    element={featureRoute("orderListing", <CommandesListingPage />)}
                  />
                  <Route
                    path="commandes-listing"
                    element={<Navigate to="/commandes-en-lot" replace />}
                  />
                  <Route
                    path="caisses"
                    element={featureRoute("cashRegisters", <CaissesPage />)}
                  />
                  <Route
                    path="statistiques"
                    element={featureRoute("statistics", <StatsPage />)}
                  />
                  <Route path="stats" element={<Navigate to="/statistiques" replace />} />
                  </Route>
                  </Route>
                </Routes>
              </ProtectedRoute>
            </NavigationHistoryProvider>
          </BrowserRouter>
        </CatalogProvider>
      </CashEntryProvider>
    </AuthProvider>
  );
}

export default App;
